import React from "react";
import { IProject } from "../../interfaces/IProject";
import { IUserSmall } from "../../interfaces/IUser";
import { Box, Card, CardContent, Grid, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IProjectState } from "../../interfaces/IProjectState";
import { ProjectDistributionChart } from "./ProjectDistributionChart";
import { IProjectLog } from "../../interfaces/IProjectLog";
import { IChartData } from "./SalesDasboard";

interface IProps {
    logArray: IProjectLog[];
    projectSmallArray: IProject[];
    projectStateArray: IProjectState[];
    currentUserSamll?: IUserSmall;
    fromDate: Date | null;
    toDate: Date | null;
    isTeam?: boolean;
    onHide?: () => void;
}

export const SalesBoardUser: React.FC<IProps> = ({
    logArray,
    projectSmallArray,
    projectStateArray,
    currentUserSamll,
    fromDate,
    toDate,
    isTeam = false,
    onHide,
}) => {
    const theme = useTheme();

    let projectByUser: IProject[];
    if (currentUserSamll) {
        // Filter projects by Worker relation instead of log entries
        projectByUser = projectSmallArray.filter(
            (project) => project.Worker?.some((worker) => worker.Profile.id === currentUserSamll.id) ?? false,
        );
    } else {
        projectByUser = projectSmallArray;
    }

    const conversionRateDenominator = projectByUser.filter(
        (project) =>
            logArray
                .filter((log) => log.to_state >= 3 && log.to_state <= 13)
                .filter((log) =>
                    fromDate === null || toDate === null
                        ? false
                        : new Date(log.created_at) >= fromDate && new Date(log.created_at) <= toDate,
                )
                .map((log) => log.Project)
                .indexOf(project.id) !== -1,
    ).length;

    const conversionRateNumerator = projectByUser.filter(
        (project) =>
            logArray
                .filter((log) => log.to_state >= 1 && log.to_state <= 13)
                .filter((log) =>
                    fromDate === null || toDate === null
                        ? false
                        : new Date(log.created_at) >= fromDate && new Date(log.created_at) <= toDate,
                )
                .map((log) => log.Project)
                .indexOf(project.id) !== -1,
    ).length;

    const getConversionRate = conversionRateDenominator / (conversionRateNumerator || 1);
    const getTotalConversionRate =
        projectByUser.length === 0
            ? 0
            : projectByUser.filter((x) => x.ProjectState === 3).length / projectByUser.length;

    const closingRateDenominator = projectByUser.filter(
        (project) =>
            logArray
                .filter((log) => log.to_state >= 5 && log.to_state <= 11)
                .filter((log) =>
                    fromDate === null || toDate === null
                        ? false
                        : new Date(log.created_at) >= fromDate && new Date(log.created_at) <= toDate,
                )
                .map((log) => log.Project)
                .indexOf(project.id) !== -1,
    ).length;

    const closingRateNumerator = projectByUser.filter(
        (project) =>
            logArray
                .filter((log) => log.to_state >= 4 && log.to_state <= 13)
                .filter((log) =>
                    fromDate === null || toDate === null
                        ? false
                        : new Date(log.created_at) >= fromDate && new Date(log.created_at) <= toDate,
                )
                .map((log) => log.Project)
                .indexOf(project.id) !== -1,
    ).length;

    const getClosingRate = closingRateDenominator / (closingRateNumerator || 1);
    const getTotalClosingRate =
        projectByUser.length === 0
            ? 0
            : projectByUser.filter((x) => x.ProjectState === 6 || x.ProjectState === 7).length / projectByUser.length;

    const chartData = (): IChartData[] => {
        return projectStateArray.map((state) => ({
            name: state.projectState,
            value: projectByUser.filter((project) => project.ProjectState === state.id).length,
        }));
    };

    return (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Card variant="outlined" sx={{ position: "relative" }}>
                {onHide && (
                    <IconButton size="small" onClick={onHide} sx={{ position: "absolute", top: 4, right: 4 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}

                <CardContent>
                    <Typography variant="h4">
                        {isTeam || currentUserSamll == undefined
                            ? "Ganzes Team"
                            : `${currentUserSamll.first_name} ${currentUserSamll.last_name}`}
                    </Typography>

                    {(currentUserSamll === undefined ||
                        [3, 4].indexOf(Number(currentUserSamll.profile?.Role)) > -1) && (
                        <>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Conversion Rate
                            </Typography>
                            <Box sx={{ display: "flex" }}>
                                <Typography sx={{ ml: 10 }} variant="h4" color={theme.palette.grey[400]}>
                                    {(getTotalConversionRate * 100).toFixed(2).replace(".", ",") + " %"}
                                </Typography>
                                <Typography sx={{ ml: 10 }} variant="h4">
                                    {(getConversionRate * 100).toFixed(2).replace(".", ",") + " %"}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {(currentUserSamll === undefined ||
                        [1, 5, 6].indexOf(Number(currentUserSamll.profile?.Role)) > -1) && (
                        <>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Abschlussquote
                            </Typography>
                            <Box sx={{ display: "flex" }}>
                                <Typography sx={{ ml: 10 }} variant="h4" color={theme.palette.grey[400]}>
                                    {(getTotalClosingRate * 100).toFixed(2).replace(".", ",") + " %"}
                                </Typography>
                                <Typography sx={{ ml: 10 }} variant="h4">
                                    {(getClosingRate * 100).toFixed(2).replace(".", ",") + " %"}
                                </Typography>
                            </Box>
                        </>
                    )}

                    <ProjectDistributionChart chartData={chartData()} />
                </CardContent>
            </Card>
        </Grid>
    );
};
